export default function Guide({ onNavigate }) {
  const sectionStyle = {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #3a3a3a'
  };

  const headingStyle = {
    color: '#A78BFA',
    fontSize: '1.5rem',
    marginBottom: '12px',
    marginTop: '24px'
  };

  const subHeadingStyle = {
    color: '#FFB84D',
    fontSize: '1.1rem',
    marginBottom: '8px',
    marginTop: '16px'
  };

  const paragraphStyle = {
    color: '#d1d5db',
    lineHeight: '1.8',
    marginBottom: '12px',
    fontSize: '0.95rem'
  };

  const listStyle = {
    color: '#d1d5db',
    lineHeight: '1.8',
    marginLeft: '20px',
    marginBottom: '12px'
  };

  const infoBoxStyle = {
    backgroundColor: '#0f0520',
    border: '1px solid rgba(109, 40, 217, 0.5)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    marginTop: '16px'
  };

  const linkStyle = {
    color: '#FFB84D',
    cursor: 'pointer',
    textDecoration: 'underline'
  };

  return (
    <div className="content-section active">
      <div className="section" style={{ maxWidth: '800px' }}>
        <h1 style={{ color: '#A78BFA', marginBottom: '12px' }}>EmberX Platform Guide</h1>
        <p style={{ color: '#9ca3af', marginBottom: '32px', fontSize: '0.95rem' }}>
          Your complete guide to using the EmberX cross-chain DeFi platform
        </p>

        {/* Table of Contents */}
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '12px' }}>📚 Table of Contents</h3>
          <ul style={{ ...listStyle, listStyleType: 'none', marginLeft: '0' }}>
            <li style={{ marginBottom: '8px' }}>
              <a href="#getting-started" style={linkStyle}>1. Getting Started</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#bridge" style={linkStyle}>2. How to Bridge Tokens</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#swap" style={linkStyle}>3. How to Swap Tokens</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#liquidity" style={linkStyle}>4. How to Provide Liquidity</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#farm" style={linkStyle}>5. How to Farm & Earn</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#register" style={linkStyle}>6. How to List Your Token</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#dashboard" style={linkStyle}>7. Understanding the Dashboard</a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <a href="#faq" style={linkStyle}>8. FAQ & Troubleshooting</a>
            </li>
          </ul>
        </div>

        {/* Section 1: Getting Started */}
        <div id="getting-started" style={sectionStyle}>
          <h2 style={headingStyle}>1. Getting Started</h2>

          <h3 style={subHeadingStyle}>What is EmberX?</h3>
          <p style={paragraphStyle}>
            EmberX is a cross-chain DeFi platform that bridges XRPL tokens to Flare Network, unlocking DeFi opportunities like swapping, liquidity provision, and yield farming for XRPL token holders.
          </p>

          <h3 style={subHeadingStyle}>What You'll Need</h3>
          <ul style={listStyle}>
            <li><strong>For Bridging XRPL → Flare:</strong> Xaman wallet with XRP for fees (0.50 XRP per bridge) and your tokens</li>
            <li><strong>For Bridging Flare → XRPL:</strong> Flare-compatible wallet (MetaMask, Rabby, etc.) with FLR for fees (50 FLR per bridge plus gas)</li>
            <li><strong>For Swapping/Liquidity/Farming:</strong> Flare-compatible wallet with FLR for gas fees (small amounts, typically less than 1 FLR per transaction)</li>
          </ul>

          <h3 style={subHeadingStyle}>Understanding Wrapped Tokens</h3>
          <p style={paragraphStyle}>
            When you bridge XRPL tokens to Flare, they become "wrapped" tokens (e.g., BAPES → wBAPES). These wrapped tokens are ERC-20 tokens on Flare Network that represent your XRPL tokens at a 1:1 ratio. You can always bridge them back to unwrap them.
          </p>
        </div>

        {/* Section 2: Bridge */}
        <div id="bridge" style={sectionStyle}>
          <h2 style={headingStyle}>2. How to Bridge Tokens</h2>

          <h3 style={subHeadingStyle}>XRPL → Flare (Wrapping)</h3>
          <p style={paragraphStyle}>
            Bridge your XRPL tokens to Flare Network to access DeFi features:
          </p>
          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('bridge')} style={linkStyle}>Bridge</span>
              {' '}tab
            </li>
            <li>Connect your Flare wallet (MetaMask, Rabby, etc.)</li>
            <li>Select the token you want to bridge from the dropdown</li>
            <li>Enter the amount to bridge</li>
            <li>Click "Bridge to Flare"</li>
            <li>The system will generate two Xaman payment requests:
              <ul style={{ marginTop: '8px' }}>
                <li><strong>Step 1:</strong> Pay 0.50 XRP bridge fee to the treasury wallet</li>
                <li><strong>Step 2:</strong> Send your tokens to the lockbox address</li>
              </ul>
            </li>
            <li>Open the first payment link in Xaman (or scan QR code on desktop)</li>
            <li>Sign the 0.50 XRP fee payment in Xaman</li>
            <li>After fee confirmation, open the second link to send your tokens</li>
            <li>Sign the token transfer in Xaman</li>
            <li>Wait for confirmation (typically 1-2 minutes)</li>
            <li>Your wrapped tokens will arrive in your Flare wallet</li>
          </ol>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Bridge Fee (XRPL → Flare):</strong> Each bridge transaction requires a 0.50 XRP fee paid to the treasury. This covers bot operations, Flare gas costs, and platform maintenance.
            </p>
          </div>

          <h3 style={subHeadingStyle}>Flare → XRPL (Unwrapping)</h3>
          <p style={paragraphStyle}>
            Bridge your wrapped tokens back to XRPL:
          </p>
          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('bridge')} style={linkStyle}>Bridge</span>
              {' '}tab
            </li>
            <li>Ensure your Flare wallet is connected</li>
            <li>Click the direction switcher (⇅) to switch to "Flare → XRPL"</li>
            <li>Select your wrapped token and enter amount</li>
            <li>Enter your XRPL wallet address (where you want to receive tokens)</li>
            <li>Click "Bridge to XRPL"</li>
            <li><strong>Step 1:</strong> Approve and pay the 50 FLR bridge fee</li>
            <li><strong>Step 2:</strong> Approve the wrapped token transfer to the vault</li>
            <li>Wait for the bridge bot to process (typically 2-3 minutes)</li>
            <li>Your unwrapped tokens will arrive at your XRPL address</li>
          </ol>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Bridge Fee (Flare → XRPL):</strong> Each bridge transaction requires a 50 FLR fee to cover XRPL transaction costs and bot operations.
            </p>
          </div>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              ⚠️ <strong>Important:</strong> Double-check your XRPL address before bridging. Sending to the wrong address cannot be reversed.
            </p>
          </div>
        </div>

        {/* Section 3: Swap */}
        <div id="swap" style={sectionStyle}>
          <h2 style={headingStyle}>3. How to Swap Tokens</h2>

          <p style={paragraphStyle}>
            Trade tokens instantly on Flare Network using our AMM (Automated Market Maker):
          </p>

          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('swap')} style={linkStyle}>Swap</span>
              {' '}tab
            </li>
            <li>Connect your Flare wallet</li>
            <li>Select the token you want to swap FROM</li>
            <li>Enter the amount</li>
            <li>Select the token you want to receive (TO)</li>
            <li>Review the estimated output amount</li>
            <li>Adjust slippage tolerance if needed (0.1%, 0.5%, or 1.0%)</li>
            <li>Click "Swap"</li>
            <li>Approve the transaction in your wallet</li>
            <li>Wait for confirmation</li>
          </ol>

          <h3 style={subHeadingStyle}>Understanding Slippage</h3>
          <p style={paragraphStyle}>
            Slippage is the difference between the expected price and the actual execution price. Higher slippage tolerance allows trades to complete even with price movement, but you may receive slightly less than estimated.
          </p>
          <ul style={listStyle}>
            <li><strong>0.1%:</strong> Low slippage, best for stable pairs</li>
            <li><strong>0.5%:</strong> Recommended default for most trades</li>
            <li><strong>1.0%:</strong> Higher slippage for volatile or low-liquidity pairs</li>
          </ul>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Trading Fees:</strong> A 0.3% fee is charged on each swap. This fee goes to liquidity providers who make trading possible.
            </p>
          </div>
        </div>

        {/* Section 4: Liquidity */}
        <div id="liquidity" style={sectionStyle}>
          <h2 style={headingStyle}>4. How to Provide Liquidity</h2>

          <p style={paragraphStyle}>
            Earn fees by providing liquidity to trading pairs:
          </p>

          <h3 style={subHeadingStyle}>Adding Liquidity</h3>
          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('liquidity')} style={linkStyle}>Liquidity</span>
              {' '}tab
            </li>
            <li>Connect your Flare wallet</li>
            <li>Select Token A (e.g., FLR)</li>
            <li>Select Token B (e.g., wBAPES)</li>
            <li>Enter amount for Token A</li>
            <li>Enter amount for Token B (must be proportional to maintain pool ratio)</li>
            <li>Click "Add Liquidity"</li>
            <li>Approve both tokens if this is your first time</li>
            <li>Confirm the transaction</li>
            <li>Receive LP (Liquidity Provider) tokens representing your share</li>
          </ol>

          <h3 style={subHeadingStyle}>Removing Liquidity</h3>
          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('liquidity')} style={linkStyle}>Liquidity</span>
              {' '}tab
            </li>
            <li>Click "Remove Liquidity" tab</li>
            <li>Select the pair you want to remove</li>
            <li>Enter the amount of LP tokens to burn (or use percentage buttons)</li>
            <li>Click "Remove"</li>
            <li>Confirm the transaction</li>
            <li>Receive both tokens back proportionally</li>
          </ol>

          <h3 style={subHeadingStyle}>Understanding Impermanent Loss</h3>
          <p style={paragraphStyle}>
            When you provide liquidity, the ratio of your tokens may change based on trading activity. If token prices diverge significantly, you could experience "impermanent loss" - meaning you'd have more value if you just held the tokens instead of providing liquidity. However, trading fees can offset this loss over time.
          </p>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Earning Fees:</strong> You earn 0.3% of every trade in your pool, proportional to your share. Fees are automatically added to your position.
            </p>
          </div>
        </div>

        {/* Section 5: Farm */}
        <div id="farm" style={sectionStyle}>
          <h2 style={headingStyle}>5. How to Farm & Earn</h2>

          <p style={paragraphStyle}>
            Stake your LP tokens to earn EMBER token rewards:
          </p>

          <h3 style={subHeadingStyle}>Staking LP Tokens</h3>
          <ol style={listStyle}>
            <li>First, provide liquidity to get LP tokens (see section above)</li>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('farm')} style={linkStyle}>Farm</span>
              {' '}tab
            </li>
            <li>Connect your Flare wallet</li>
            <li>Select the farm pool</li>
            <li>Enter the amount of LP tokens to stake</li>
            <li>Click "Stake"</li>
            <li>Approve and confirm the transaction</li>
            <li>Start earning EMBER rewards automatically</li>
          </ol>

          <h3 style={subHeadingStyle}>Claiming Rewards</h3>
          <p style={paragraphStyle}>
            Your EMBER rewards accumulate over time. You can claim them anytime by clicking "Harvest" on the farm page.
          </p>

          <h3 style={subHeadingStyle}>Unstaking</h3>
          <p style={paragraphStyle}>
            To remove your LP tokens from farming, click "Unstake" and enter the amount. Your unclaimed rewards will automatically be harvested when you unstake.
          </p>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Compound Returns:</strong> You can swap your earned EMBER back to LP tokens and restake to maximize returns.
            </p>
          </div>
        </div>

        {/* Section 6: Register */}
        <div id="register" style={sectionStyle}>
          <h2 style={headingStyle}>6. How to List Your Token</h2>

          <p style={paragraphStyle}>
            Get your XRPL token listed for bridging on EmberX:
          </p>

          <h3 style={subHeadingStyle}>Listing Process</h3>
          <ol style={listStyle}>
            <li>Navigate to the{' '}
              <span onClick={() => onNavigate('register')} style={linkStyle}>List Token</span>
              {' '}tab
            </li>
            <li>Fill in your token details:
              <ul style={{ marginTop: '8px' }}>
                <li>Token Ticker (symbol)</li>
                <li>Token Name</li>
                <li>XRPL Issuer Address</li>
                <li>XRPL Currency Code</li>
                <li>Token Decimals (if applicable)</li>
                <li>Token Logo (optional, but recommended)</li>
              </ul>
            </li>
            <li>Review the listing fee (50 XRP)</li>
            <li>Click "Continue to Payment"</li>
            <li>Complete payment in Xaman wallet</li>
            <li>Submit your listing</li>
            <li>Wait for admin approval (typically 24-48 hours)</li>
          </ol>

          <h3 style={subHeadingStyle}>After Approval</h3>
          <p style={paragraphStyle}>
            Once approved, your token will:
          </p>
          <ul style={listStyle}>
            <li>Appear on the{' '}
              <span onClick={() => onNavigate('leaderboard')} style={linkStyle}>Leaderboard</span>
            </li>
            <li>Be available for bridging in the Bridge section</li>
            <li>Automatically receive a wrapped contract on Flare Network</li>
            <li>Be tradeable on the Swap section</li>
          </ul>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              💡 <strong>Listing Fee:</strong> The 50 XRP fee covers contract deployment, security audits, and platform integration costs.
            </p>
          </div>
        </div>

        {/* Section 7: Dashboard */}
        <div id="dashboard" style={sectionStyle}>
          <h2 style={headingStyle}>7. Understanding the Dashboard</h2>

          <p style={paragraphStyle}>
            The{' '}
            <span onClick={() => onNavigate('dashboard')} style={linkStyle}>Dashboard</span>
            {' '}gives you a complete overview of your Flare Network holdings:
          </p>

          <h3 style={subHeadingStyle}>What You'll See</h3>
          <ul style={listStyle}>
            <li><strong>Native Tokens:</strong> FLR, WFLR, and EMBER balances</li>
            <li><strong>Wrapped XRPL Tokens:</strong> All your bridged tokens (wBAPES, etc.)</li>
            <li><strong>Token Logos:</strong> Visual identification for easy tracking</li>
            <li><strong>Real-time Balances:</strong> Live balance updates from the blockchain</li>
          </ul>

          <p style={paragraphStyle}>
            Use the Dashboard to quickly check your portfolio before making trades or providing liquidity.
          </p>
        </div>

        {/* Section 8: FAQ */}
        <div id="faq" style={sectionStyle}>
          <h2 style={headingStyle}>8. FAQ & Troubleshooting</h2>

          <h3 style={subHeadingStyle}>How long does bridging take?</h3>
          <p style={paragraphStyle}>
            Typically 1-3 minutes total. XRPL → Flare usually takes 1-2 minutes after you complete both Xaman payment steps. Flare → XRPL takes 2-3 minutes after you approve both the FLR fee and token transfer. Times may vary depending on network conditions.
          </p>

          <h3 style={subHeadingStyle}>What if my bridge transaction is stuck?</h3>
          <p style={paragraphStyle}>
            First, check that your transaction was confirmed on the source blockchain. If it's been over 10 minutes, contact support at support@EmberX.xyz with your transaction hash.
          </p>

          <h3 style={subHeadingStyle}>Do I need FLR to use the platform?</h3>
          <p style={paragraphStyle}>
            Yes, you need FLR for two purposes on Flare Network:
          </p>
          <ul style={listStyle}>
            <li><strong>Bridge Fee (Flare → XRPL only):</strong> 50 FLR per bridge transaction</li>
            <li><strong>Gas Fees:</strong> Small amounts (typically &lt;1 FLR) for all transactions: swapping, adding/removing liquidity, farming, and token approvals</li>
          </ul>
          <p style={paragraphStyle}>
            Note: Bridging from XRPL → Flare does NOT require FLR - only XRP for the bridge fee.
          </p>

          <h3 style={subHeadingStyle}>Why do I need to approve tokens?</h3>
          <p style={paragraphStyle}>
            Token approvals are a security feature of ERC-20 tokens. You're giving the smart contract permission to interact with your tokens. This is a one-time action per token per contract.
          </p>

          <h3 style={subHeadingStyle}>Are my funds safe?</h3>
          <p style={paragraphStyle}>
            All wrapped tokens are backed 1:1 by XRPL tokens held in secure vaults. Our smart contracts follow industry best practices. However, DeFi carries inherent risks - never invest more than you can afford to lose.
          </p>

          <h3 style={subHeadingStyle}>What is the total value locked (TVL)?</h3>
          <p style={paragraphStyle}>
            TVL shows the total value of tokens locked in our XRPL vaults. Higher TVL indicates more trust and usage of the platform. You can see per-token TVL on the{' '}
            <span onClick={() => onNavigate('leaderboard')} style={linkStyle}>Leaderboard</span>.
          </p>

          <h3 style={subHeadingStyle}>Can I cancel a bridge transaction?</h3>
          <p style={paragraphStyle}>
            Once a transaction is confirmed on the blockchain, it cannot be cancelled. Always double-check amounts and addresses before confirming.
          </p>

          <h3 style={subHeadingStyle}>How are bridge fees calculated?</h3>
          <p style={paragraphStyle}>
            Bridge fees are fixed amounts that cover bot operations, blockchain transaction costs, and platform maintenance:
          </p>
          <ul style={listStyle}>
            <li><strong>XRPL → Flare:</strong> 0.50 XRP per transaction</li>
            <li><strong>Flare → XRPL:</strong> 50 FLR per transaction</li>
          </ul>
          <p style={paragraphStyle}>
            These fees are separate from the tokens you're bridging, ensuring your full token amount transfers while covering operational costs.
          </p>

          <div style={infoBoxStyle}>
            <p style={{ color: '#C4B5FD', margin: 0, fontSize: '0.875rem' }}>
              📧 <strong>Still need help?</strong> Contact us at{' '}
              <a href="mailto:support@EmberX.xyz" style={{ color: '#FFB84D', textDecoration: 'underline' }}>
                support@EmberX.xyz
              </a>
              {' '}or reach out on X{' '}
              <a href="https://x.com/EmberXFi" target="_blank" rel="noopener noreferrer" style={{ color: '#FFB84D', textDecoration: 'underline' }}>
                @EmberXFi
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '1px solid #3a3a3a',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <span
              onClick={() => onNavigate && onNavigate('terms')}
              style={{
                color: '#A78BFA',
                textDecoration: 'none',
                marginRight: '24px',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Terms & Conditions
            </span>
            <span
              onClick={() => onNavigate && onNavigate('privacy')}
              style={{
                color: '#A78BFA',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Privacy Policy
            </span>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <span style={{ fontWeight: '600', color: '#fff' }}>Contact Us: </span>
            <a
              href="mailto:support@EmberX.xyz"
              style={{
                color: '#FFB84D',
                textDecoration: 'none',
                marginRight: '16px'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              ✉️ support@EmberX.xyz
            </a>
            <span style={{ marginRight: '8px' }}>|</span>
            <a
              href="https://x.com/EmberXFi"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#FFB84D',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '2px' }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              @EmberXFi
            </a>
          </div>
          <p style={{ margin: '0' }}>
            © 2025 EmberX. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
